<!-- src/views/ProductsView.vue -->
<template>
  <div class="main-background">
    <!-- Product Dialog -->
    <ProductDialog
      v-model="dialogVisible"
      :dialogData="dialogData"
      @save="handleSave"
      @delete="handleDelete"
      @close="closeDialog"
    />

    <v-container>
      <v-row>
        <!-- Search Bar -->
        <v-col cols="8">
          <v-text-field
            v-model="search"
            density="compact"
            label="Search"
            prepend-inner-icon="mdi-magnify"
            variant="solo-filled"
            flat
            single-line
          />
        </v-col>

        <!-- Actions (Edit/Create buttons) -->
        <v-col cols="4" class="d-flex justify-end">
          <v-btn @click="toggleEditMode" class="mb-5 ml-2">
            {{ isEditMode ? "Back" : "Edit" }}
          </v-btn>
          <v-btn @click="toggleCreateMode" class="mb-5 ml-2">Créer</v-btn>
        </v-col>
      </v-row>

      <!-- Data Table -->
      <v-data-table-server
        :headers="headers"
        :items="products"
        :items-per-page="options.itemsPerPage"
        :page="options.page"
        :items-length="totalItems"
        :loading="loading"
        @update:options="loadProducts"
      >
        <template v-slot:item="{ item }">
          <tr @click="isEditMode && handleRowClick(item)">
            <td>{{ item["name"] }}</td>
          </tr>
        </template>
      </v-data-table-server>
    </v-container>
  </div>
</template>

<script>
import { ref, reactive, watch, onMounted } from 'vue';
import axiosInstance from "@/plugins/axios.js";
import debounce from "lodash/debounce";
import ProductDialog from "./ProductDialog.vue";

export default {
  components: { ProductDialog },
  setup() {
    const products = ref([]);
    const unitsOfMeasure = ref([]);
    const search = ref('');
    const isEditMode = ref(false);
    const dialogVisible = ref(false);
    const options = ref({ page: 1, itemsPerPage: 10 });
    const loading = ref(false);
    const totalItems = ref(0);
    const headers = [
      { title: "Name", key: "name" },
      { title: "Unit", key: "unit" },
    ];

    const dialogData = reactive({
      itemToEdit: {
        name:'',
        unitId:null,
        barcode:'',
      },
      unitsOfMeasure: unitsOfMeasure,
      isCreate: false,
    });

    const debouncedFetchData = debounce(() => loadProducts(options.value), 700);

    const loadProducts = async (newOptions = options.value) => {
      loading.value = true;
      try {
        const { page, itemsPerPage } = newOptions;
        const response = await axiosInstance.get("/Product", {
          params: { pageNumber: page, pageSize: itemsPerPage, search: search.value },
        });

        products.value = response.data.products;
        totalItems.value = response.data.pagination.totalProducts;

        options.value = { ...options.value, ...newOptions };
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        loading.value = false;
      }
    };

    const loadUnitsOfMeasure = async () => {
      const response = await axiosInstance.get("/UnitOfMeasure");
      unitsOfMeasure.value = response.data;
    };

    //Open dialog on row click 
    const handleRowClick = (item) => {
      dialogData.isCreate = false;
      dialogData.itemToEdit = {...item};
      dialogVisible.value = true;
    };

    const closeDialog = () => {
      dialogVisible.value = false;
      dialogData.itemToEdit = {};
    };

    const toggleEditMode = () => {
      isEditMode.value = !isEditMode.value;
    };

    const toggleCreateMode = () => {
      dialogData.isCreate = true;
      dialogVisible.value = true;
    };

    const handleSave = async (updatedItem, isCreate) => {
      const method = isCreate ? axiosInstance.post : axiosInstance.put;
      const url = "/Product";
      await method(url, updatedItem);
      loadProducts(options.value);
      dialogVisible.value = false;
    };

    const handleDelete = async (product) => {
      await axiosInstance.delete(`/Product/${product.id}`);
      loadProducts(options.value);
      dialogVisible.value = false;
    };

    // Watchers
    watch(search, debouncedFetchData);

    // Lifecycle hooks
    onMounted(() => {
      loadProducts();
      loadUnitsOfMeasure();
    });

    return {
      products,
      unitsOfMeasure,
      search,
      isEditMode,
      dialogVisible,
      options,
      loading,
      totalItems,
      headers,
      loadProducts,
      loadUnitsOfMeasure,
      handleRowClick,
      closeDialog,
      toggleEditMode,
      toggleCreateMode,
      handleSave,
      handleDelete,
      dialogData
    };
  }
};
</script>

<style scoped>
.edit-mode tbody tr:hover {
  background-color: #e0e0e0;
  cursor: pointer;
}
.hover-highlight tbody tr {
  background-color: #f0f0f0;
}
</style>
